import React, { ComponentType } from "react"
import { Route } from "react-router-dom"
import { withRouter } from "QuorumGrassroots/withRouter"

/**
 * Route Registry System
 *
 * This system allows features to register custom routes without modifying
 * the core framework routing code. Routes are registered in a central registry
 * and injected into the main routing system.
 */

export interface RouteConfig {
    /**
     * The path pattern for the route (e.g., "/campaign/:id/key-dates")
     * Uses React Router path syntax
     */
    path: string

    /**
     * The component to render for this route
     */
    component: ComponentType<any>

    /**
     * Whether this route requires authentication
     * @default false
     */
    requiresLogin?: boolean

    /**
     * Page title to display in browser tab
     */
    pageTitle?: string

    /**
     * Unique key for the route (used by React Router)
     * If not provided, will use the path
     */
    key?: string
}

/**
 * Central registry for custom routes
 * Features can add their routes here
 */
class RouteRegistryClass {
    private routes: RouteConfig[] = []

    /**
     * Register a single route
     */
    register(route: RouteConfig): void {
        this.routes.push(route)
    }

    /**
     * Register multiple routes at once
     */
    registerMany(routes: RouteConfig[]): void {
        this.routes.push(...routes)
    }

    /**
     * Get all registered routes
     */
    getAll(): RouteConfig[] {
        return this.routes
    }

    /**
     * Clear all registered routes (useful for testing)
     */
    clear(): void {
        this.routes = []
    }

    /**
     * Generate React Router route elements from all registered routes
     *
     * This method should be called in the main routing component to inject
     * all registered routes into the routing system.
     *
     * @returns Array of React Router Route elements
     */
    generateRoutes(): JSX.Element[] {
        return this.routes.map((routeConfig) => {
            const RouteComponent = routeConfig.component
            const ComponentWithRouter = withRouter(RouteComponent)

            return (
                <Route
                    key={routeConfig.key || routeConfig.path}
                    path={routeConfig.path}
                    element={<ComponentWithRouter />}
                />
            )
        })
    }
}

// Export singleton instance
export const RouteRegistry = new RouteRegistryClass()
